// CapabilityMatrix — per-module verb columns + extra-action pills. Structure
// comes from the module catalog (GET /modules); the enabled state comes via the
// isEnabled predicate. Read-only by default; `editable` + `onToggle` turn the
// cells/pills into toggles (the cascade rules live with the caller — see
// toggleWithCascade in rolesApi).

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Chip, Icon } from '@/components/common';
import { MODULE_PARENT_SLUG, VERBS, type MatrixModule } from '@/api/endpoints/rolesApi';

interface CapabilityMatrixProps {
  modules: MatrixModule[];
  /** Whether a permission (by id) is enabled for the role. */
  isEnabled: (permId: string) => boolean;
  /** Allow toggling cells/pills. */
  editable?: boolean;
  onToggle?: (module: MatrixModule, permId: string) => void;
}

const CapabilityMatrix = ({
  modules,
  isEnabled,
  editable = false,
  onToggle,
}: CapabilityMatrixProps) => {
  const { t } = useTranslation();

  // Nested modules (MODULE_PARENT_SLUG) leave the top-level sequence and render
  // as indented sub-rows right after their parent; a child whose parent is not
  // in this platform's list falls back to a normal top-level row.
  const rows = useMemo(() => {
    const slugs = new Set(modules.map((m) => m.slug));
    const out: { module: MatrixModule; sub: boolean }[] = [];
    modules.forEach((m) => {
      const parent = MODULE_PARENT_SLUG[m.slug];
      if (parent && slugs.has(parent)) return; // rendered under its parent below
      out.push({ module: m, sub: false });
      modules
        .filter((c) => MODULE_PARENT_SLUG[c.slug] === m.slug)
        .forEach((c) => out.push({ module: c, sub: true }));
    });
    return out;
  }, [modules]);

  const extraChip = (enabled: boolean, title: string) =>
    enabled ? (
      <Chip variant='brand-solid' iconStart='check'>
        {title}
      </Chip>
    ) : (
      <Chip variant='neutral' iconStart='plus'>
        {title}
      </Chip>
    );

  return (
    <div className='roles-matrix'>
      <div className='roles-matrix__head'>
        <span>{t('roles_col_module')}</span>
        <span>{t('roles_col_view')}</span>
        <span>{t('roles_col_create')}</span>
        <span>{t('roles_col_edit')}</span>
        <span>{t('roles_col_delete')}</span>
        <span>{t('roles_col_extra')}</span>
      </div>

      {rows.map(({ module: m, sub }) => {
        return (
          <div
            className={sub ? 'roles-matrix__row roles-matrix__row--sub' : 'roles-matrix__row'}
            key={m.slug}
          >
            <span className='roles-matrix__module'>
              {sub && (
                <Icon
                  name='corner-down-right'
                  size={16}
                  className='roles-matrix__sub-glyph'
                  aria-hidden='true'
                />
              )}
              {m.title}
              {sub && (
                <Chip variant='outline' iconStart='link' className='roles-matrix__dep-chip'>
                  {t('roles_needs_forms_view')}
                </Chip>
              )}
            </span>

            {VERBS.map((verb) => {
              const perm = m.verbs.find((p) => p.slug === verb);
              return (
                <span key={verb} className='roles-matrix__cell'>
                  {perm ? (
                    <Checkbox
                      checked={isEnabled(perm.id)}
                      aria-label={`${m.title} — ${perm.title}`}
                      {...(editable
                        ? { onChange: () => onToggle?.(m, perm.id) }
                        : { readOnly: true })}
                    />
                  ) : (
                    <span className='roles-matrix__dash'>—</span>
                  )}
                </span>
              );
            })}

            <span className='roles-matrix__extras'>
              {m.extras.length === 0 ? (
                <span className='roles-matrix__dash'>—</span>
              ) : (
                m.extras.map((ex) =>
                  editable ? (
                    <button
                      key={ex.id}
                      type='button'
                      className='roles-matrix__pill'
                      aria-pressed={isEnabled(ex.id)}
                      onClick={() => onToggle?.(m, ex.id)}
                    >
                      {extraChip(isEnabled(ex.id), ex.title)}
                    </button>
                  ) : (
                    <span key={ex.id}>{extraChip(isEnabled(ex.id), ex.title)}</span>
                  ),
                )
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default CapabilityMatrix;
